import { PenLine, RotateCw } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  FAILURE_CAUSE_INFO,
  MESSAGE_ACTION_REFUSAL_TEXT,
  type FailureCause,
  type FailureGroups,
  type ResendAllResult,
} from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { errorMessage } from '../../../../lib/api-client';
import { useToasts } from '../../../../stores/toasts';
import { useResendAll } from '../../api/email-delivery-api';
import { ConfirmBeside } from '../ConfirmBeside';
import { plural } from './email-delivery-address';
import styles from './FailureCauses.module.css';

/**
 * "Why they failed", grouped by cause with one button each (Option A, taken from B). An address problem offers Fix
 * address on its newest email; anything else, Send again for the whole group. "Send all again" goes through the
 * same rules email by email, and says what it left out and why.
 */
export function FailureCauses({
  groups,
  canEdit,
  onOpen,
  onResult,
}: {
  readonly groups: FailureGroups;
  readonly canEdit: boolean;
  readonly onOpen: (id: string) => void;
  readonly onResult: (result: ResendAllResult) => void;
}): ReactNode {
  const resendAll = useResendAll();
  const showToast = useToasts((state) => state.show);
  if (groups.groups.length === 0) return null;

  const run = (cause?: FailureCause): void =>
    resendAll.mutate(cause ? { cause } : {}, {
      onSuccess: (result) => {
        onResult(result);
        showToast({ tone: result.resent > 0 ? 'success' : 'info', message: result.resent > 0 ? `${plural(result.resent, 'email', 'emails')} sent again` : 'Nothing was sent again', ...(result.skipped.length > 0 ? { detail: `${plural(result.skipped.length, 'was', 'were')} left out; the list says why.` } : {}) });
      },
      onError: (error) => showToast({ tone: 'danger', message: 'Nothing was sent again', detail: errorMessage(error) }),
    });

  return (
    <section className={styles.causes} aria-label="Why they failed">
      <div className={styles.head}>
        <h2 className={styles.title}>Why they failed</h2>
        <span className={styles.sub}>Grouped by cause, one button each</span>
        <span className={styles.grow} />
        {canEdit && (
          <ConfirmBeside
            label={`Send all ${groups.total.toLocaleString()} again`}
            icon={RotateCw}
            isPending={resendAll.isPending}
            title={`Send all ${groups.total.toLocaleString()} again?`}
            body="Each goes through the same checks as Send again. Emails with a wrong or mistyped address, a sign-in link, or an anonymised person are left out, and listed. They can't be taken back once they have gone."
            confirmLabel="Send them"
            onConfirm={() => run()}
          />
        )}
      </div>
      <ul className={styles.cards}>
        {groups.groups.map((group) => {
          const info = FAILURE_CAUSE_INFO[group.cause];
          const example = group.examples[0];
          return (
            <li key={group.cause} className={styles.card}>
              <div className={styles.cardHead}>
                <b className={styles.cardTitle}>{info.label}</b>
                <span className={styles.count}>{group.count.toLocaleString()}</span>
              </div>
              <span className={styles.sub}>
                {info.isAddressProblem && example ? `${example.toAddress}${example.suggestedAddress ? ' — looks mistyped' : ''}` : info.isTemporary ? 'Usually clears on its own' : info.advice}
              </span>
              {canEdit && info.isAddressProblem && example && (
                <Button size="sm" icon={PenLine} onClick={() => onOpen(example.id)}>
                  Fix address…
                </Button>
              )}
              {canEdit && !info.isAddressProblem && group.cause !== 'not_written' && (
                <ConfirmBeside
                  label={group.count === 1 ? 'Send again' : `Send ${group.count.toLocaleString()} again`}
                  icon={RotateCw}
                  isPending={resendAll.isPending}
                  title={`Send ${plural(group.count, 'email', 'emails')} again?`}
                  body={`Every email that failed because “${info.label.toLowerCase()}” goes again, through the same checks as Send again.`}
                  confirmLabel="Send them"
                  onConfirm={() => run(group.cause)}
                />
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** What "Send all again" left out, and why — shown until dismissed. */
export function ResendReport({ result, onOpen, onDismiss }: { readonly result: ResendAllResult; readonly onOpen: (id: string) => void; readonly onDismiss: () => void }): ReactNode {
  return (
    <section className={styles.report} aria-label="What was sent again">
      <div className={styles.head}>
        <h2 className={styles.title}>
          {plural(result.resent, 'email', 'emails')} sent again
          {result.skipped.length > 0 ? `, ${result.skipped.length.toLocaleString()} left out` : ''}
        </h2>
        <span className={styles.grow} />
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          Close
        </Button>
      </div>
      {result.remaining > 0 && <p className={styles.sub}>{plural(result.remaining, 'more is', 'more are')} still waiting. Click Send all again once more for the rest.</p>}
      {result.skipped.length > 0 && (
        <ul className={styles.skipped}>
          {result.skipped.map((row) => (
            <li key={row.id}>
              <button type="button" className={styles.skippedRow} onClick={() => onOpen(row.id)}>
                <b>{row.toAddress}</b> · {row.subject}
              </button>
              <span className={styles.sub}>{MESSAGE_ACTION_REFUSAL_TEXT[row.refusal]}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
